import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MessageSquare, PlusCircle } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Page, PageHeader, StatusBadge, Empty } from "@/components/common";
import BookingDetail from "@/components/BookingDetail";
import { useAuth } from "@/context/AuthContext";
import { api, fmtDate, errMsg, STATUSES } from "@/lib/api";
import { ManualBookingDialog } from "@/components/ManualBookingDialog";
import { PrintButton } from "@/components/PrintButton";
import { BookingQuickActions } from "@/components/BookingQuickActions";

function Pick({ value, onChange, options, testId }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="w-full sm:w-44" data-testid={testId}><SelectValue /></SelectTrigger>
      <SelectContent>{options.map((o) => <SelectItem key={o.value} value={o.value} data-testid={`${testId}-option-${o.value}`}>{o.label}</SelectItem>)}</SelectContent>
    </Select>
  );
}

const EMPTY = { q: "", teacher: "all", subject: "all", status: "all", from: "", to: "" };
const periodLabel = (lesson) => (lesson.periods || [lesson.period])
  .map((period) => `P${period}`)
  .join("–");

export default function BookingsPage() {
  const { can, user } = useAuth();
  const [items, setItems] = useState([]);
  const [all, setAll] = useState(false);
  const [past, setPast] = useState(false);
  const [open, setOpen] = useState(null);
  const [manualOpen, setManualOpen] = useState(false);
  const [repeatSource, setRepeatSource] = useState(null);
  const [f, setF] = useState(EMPTY);
  const set = (k) => (v) => setF((o) => ({ ...o, [k]: v }));
  const today = new Date().toISOString().slice(0, 10);
  const statusOptions = all ? STATUSES.filter((status) => status !== "Cancelled") : STATUSES;
  const load = useCallback(() => {
    api.get("/bookings", { params: { scope: all ? "all" : "mine", from_date: f.from || (past ? "" : today), to_date: f.to } }).then((r) => setItems(r.data));
  }, [all, past, today, f.from, f.to]);
  useEffect(() => {
    load();
  }, [load]);

  const teachers = [...new Map(items.map((b) => [b.teacher_id, b.teacher_name]))].sort((a, b) => a[1].localeCompare(b[1]));
  const subjects = [...new Set(["Biology", "Chemistry", "Physics", ...items.map((b) => b.practical.subject).filter(Boolean)])].sort();
  const ql = f.q.trim().toLowerCase();
  const shown = items.filter((b) => (f.teacher === "all" || b.teacher_id === f.teacher)
    && (f.subject === "all" || b.practical.subject === f.subject)
    && (f.status === "all" || b.status === f.status)
    && (!ql || `${b.reference} ${b.practical.name} ${b.teacher_name} ${b.lesson.class_name} ${b.lesson.lab} ${b.practical.subject || ""} ${b.status}`.toLowerCase().includes(ql)));
  const filtered = JSON.stringify(f) !== JSON.stringify(EMPTY);

  return (
    <Page wide>
      <PageHeader title={all ? "All department bookings" : "My bookings"} subtitle="Live status from the prep room. Book manually, or just ask Jonny.">
        <PrintButton label="Print current bookings" testId="bookings-print-button" />
        {can("booking:create") && <Button variant="outline" onClick={() => { setRepeatSource(null); setManualOpen(true); }} data-testid="bookings-book-manually-button"><PlusCircle className="h-4 w-4 mr-2" />Book manually</Button>}
        <Button asChild data-testid="bookings-ask-jonny-button"><Link to="/chat"><MessageSquare className="h-4 w-4 mr-2" />Book with Jonny</Link></Button>
      </PageHeader>
      <div className="flex flex-col sm:flex-row sm:flex-wrap gap-2 sm:items-center mb-4" data-testid="bookings-filters">
        <Input placeholder="Search reference, practical, class, lab…" value={f.q} onChange={(e) => set("q")(e.target.value)} className="sm:w-72" data-testid="bookings-search-input" />
        {all && <Pick value={f.teacher} onChange={set("teacher")} testId="bookings-filter-teacher" options={[{ value: "all", label: "All teachers" }, ...teachers.map(([id, n]) => ({ value: id, label: n }))]} />}
        <Pick value={f.subject} onChange={set("subject")} testId="bookings-filter-subject" options={[{ value: "all", label: "All subjects" }, ...subjects.map((s) => ({ value: s, label: s }))]} />
        <Pick value={f.status} onChange={set("status")} testId="bookings-filter-status" options={[{ value: "all", label: "All statuses" }, ...statusOptions.map((s) => ({ value: s, label: s }))]} />
        <div className="flex items-center gap-2 text-sm">
          <Input type="date" value={f.from} onChange={(e) => set("from")(e.target.value)} className="w-40" aria-label="From date" data-testid="bookings-filter-from" />
          <span className="text-muted-foreground">to</span>
          <Input type="date" value={f.to} onChange={(e) => set("to")(e.target.value)} className="w-40" aria-label="To date" data-testid="bookings-filter-to" />
        </div>
        <label className="flex items-center gap-2 text-sm"><Switch checked={past} onCheckedChange={setPast} data-testid="bookings-show-past-switch" />Include past</label>
        {can("booking:read_all") && <label className="flex items-center gap-2 text-sm"><Switch checked={all} onCheckedChange={(enabled) => { setAll(enabled); if (enabled && f.status === "Cancelled") set("status")("all"); }} data-testid="bookings-show-all-switch" />Whole department</label>}
        {filtered && <Button variant="ghost" size="sm" onClick={() => setF(EMPTY)} data-testid="bookings-filter-clear">Clear filters</Button>}
        <span className="text-xs text-muted-foreground sm:ml-auto" data-testid="bookings-filter-count">{shown.length} booking{shown.length === 1 ? "" : "s"}</span>
      </div>
      {items.length === 0 && !filtered ? <Empty>No bookings yet. Book manually above, or ask Jonny: “Book an osmosis practical for my next Year 8 lesson.”</Empty> : shown.length === 0 ? <Empty>No bookings match these filters.</Empty> : (
        <div className="rounded-xl border bg-card divide-y" data-testid="bookings-list">
          {shown.map((b) => {
            const canCancel = !["Cancelled", "Completed"].includes(b.status)
              && (b.teacher_id === user.id || can("booking:cancel_any"));
            const canRepeat = can("booking:create") && b.status !== "Cancelled";
            const canRemove = !all && b.status === "Cancelled" && b.teacher_id === user.id;
            return (
              <div
                key={b.id}
                className="flex flex-col gap-3 p-4 transition-colors hover:bg-muted/40 sm:flex-row sm:items-center"
                data-testid={`booking-row-${b.reference}`}
              >
                <button
                  type="button"
                  onClick={() => setOpen(b.id)}
                  className="flex min-w-0 flex-1 flex-col gap-3 text-left sm:flex-row sm:items-center"
                  data-testid={`booking-open-${b.reference}`}
                >
                  <div className="shrink-0 sm:w-36">
                    <div className="text-sm font-semibold">{fmtDate(b.lesson.date)}</div>
                    <div className="text-xs text-muted-foreground">
                      {b.lesson.week ? `Week ${b.lesson.week} · ` : ""}{periodLabel(b.lesson)}
                    </div>
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="truncate font-medium">{b.practical.name}</div>
                    <div className="text-xs text-muted-foreground">
                      {[
                        b.practical.subject,
                        b.lesson.class_name,
                        b.lesson.lab,
                        `${b.sets} set${b.sets === 1 ? "" : "s"}`,
                        all ? b.teacher_name : null,
                      ].filter(Boolean).join(" · ")}
                    </div>
                  </div>
                </button>
                <span className="font-mono text-xs text-muted-foreground">{b.reference}</span>
                <StatusBadge status={b.status} testId={`booking-status-${b.reference}`} />
                <BookingQuickActions
                  booking={b}
                  canCancel={canCancel}
                  canRepeat={canRepeat}
                  canRemove={canRemove}
                  onCancelled={load}
                  onRemoved={load}
                  onRepeat={() => {
                    setRepeatSource(b);
                    setManualOpen(true);
                  }}
                />
              </div>
            );
          })}
        </div>
      )}
      <BookingDetail
        bookingId={open}
        onClose={() => setOpen(null)}
        mode="teacher"
        onChanged={load}
        onRepeatForLesson={(booking) => {
          setOpen(null);
          setRepeatSource(booking);
          setManualOpen(true);
        }}
      />
      <ManualBookingDialog
        open={manualOpen}
        initialBooking={repeatSource}
        onOpenChange={(nextOpen) => {
          setManualOpen(nextOpen);
          if (!nextOpen) setRepeatSource(null);
        }}
        onBooked={() => {
          setRepeatSource(null);
          load();
        }}
      />
    </Page>
  );
}
